import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DAYS, MEAL_TYPES } from './WeekView';

function getISOWeekString(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

function getCalendarDays(year, month) {
  const firstDay = new Date(Date.UTC(year, month, 1));
  const lastDay = new Date(Date.UTC(year, month + 1, 0));
  // Monday=1 ... Sunday=7
  const startDow = firstDay.getUTCDay() || 7;
  const startDate = new Date(firstDay);
  startDate.setUTCDate(startDate.getUTCDate() - (startDow - 1));

  const days = [];
  const d = new Date(startDate);
  // Always show 6 rows (42 days) for consistent grid
  while (days.length < 42) {
    days.push(new Date(d));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  // Trim trailing week if all days are in next month
  if (days.length > 35 && days[35].getUTCMonth() !== month) {
    days.splice(35, 7);
  }
  return days;
}

function getWeeksForMonth(calendarDays) {
  const weeks = new Set();
  for (const d of calendarDays) {
    weeks.add(getISOWeekString(d));
  }
  return [...weeks];
}

function MonthView({ year, month, apiFetch, onDayClick }) {
  const { t, i18n } = useTranslation();
  const [weekMeals, setWeekMeals] = useState({});
  const todayStr = new Date().toISOString().slice(0, 10);

  const calendarDays = useMemo(() => getCalendarDays(year, month), [year, month]);
  const neededWeeks = useMemo(() => getWeeksForMonth(calendarDays), [calendarDays]);

  const fetchWeekMeals = useCallback(async (weekStr) => {
    try {
      const res = await apiFetch(`/api/meals?week=${weekStr}`);
      const data = await res.json();
      return { week: weekStr, meals: data };
    } catch {
      return { week: weekStr, meals: null };
    }
  }, [apiFetch]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const toFetch = neededWeeks.filter((w) => !weekMeals[w]);
      if (toFetch.length === 0) return;
      const results = await Promise.all(toFetch.map(fetchWeekMeals));
      if (cancelled) return;
      setWeekMeals((prev) => {
        const next = { ...prev };
        for (const r of results) {
          next[r.week] = r.meals;
        }
        return next;
      });
    };
    load();
    return () => { cancelled = true; };
  }, [neededWeeks, fetchWeekMeals]); // eslint-disable-line react-hooks/exhaustive-deps

  const getMealsForDate = (date) => {
    const weekStr = getISOWeekString(date);
    const meals = weekMeals[weekStr];
    if (!meals) return null;
    const dayIndex = (date.getUTCDay() + 6) % 7; // 0=Mon
    const dayKey = DAYS[dayIndex];
    return meals[dayKey];
  };

  const dayHeaders = useMemo(() => {
    return DAYS.map((_, i) => {
      const d = new Date(Date.UTC(2024, 0, i + 1)); // Jan 1 2024 is a Monday
      return d.toLocaleDateString(i18n.language, { weekday: 'short', timeZone: 'UTC' });
    });
  }, [i18n.language]);

  const monthLabel = new Date(Date.UTC(year, month, 1)).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric', timeZone: 'UTC' });

  return (
    <div className="month-view">
      <div className="month-grid-header">
        {dayHeaders.map((label, i) => (
          <div key={i} className="month-day-header">{label}</div>
        ))}
      </div>
      <div className="month-grid" style={{ gridTemplateRows: `repeat(${calendarDays.length / 7}, 1fr)` }}>
        {calendarDays.map((date, i) => {
          const dateStr = date.toISOString().slice(0, 10);
          const isCurrentMonth = date.getUTCMonth() === month;
          const isToday = dateStr === todayStr;
          const dayMeals = getMealsForDate(date);

          return (
            <div
              key={i}
              className={`month-cell${isCurrentMonth ? '' : ' other-month'}${isToday ? ' today' : ''}`}
              onClick={() => onDayClick(date)}
            >
              <div className="month-cell-day">{date.getUTCDate()}</div>
              {dayMeals && (
                <div className="month-cell-meals">
                  {MEAL_TYPES.map((mt) => {
                    const slots = dayMeals[mt];
                    const slotsArr = Array.isArray(slots) ? slots : (slots ? [slots] : []);
                    const names = slotsArr.map((s) => s.name).filter(Boolean);
                    if (names.length === 0) return null;
                    return (
                      <div key={mt} className={`month-meal-entry ${mt}`}>
                        <span className="month-meal-icon">{mt === 'lunch' ? '☀️' : '🌙'}</span>
                        <span className="month-meal-names">{names.join(', ')}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MonthView;
